import React from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, StarIcon } from '@heroicons/react/24/outline';

const PricingSelection = ({ formData, updateFormData, onNext }) => {
  const plans = [
    {
      id: 'free',
      name: 'Free',
      price: '$0',
      period: 'per member / month',
      description: 'Perfect for trying out Klorah Teams',
      features: [
        '5 collaborative projects',
        'Basic client management',
        'Photo sharing gallery',
        'Email support',
        'Up to 3 team members'
      ],
      limitations: [
        'Limited storage (5GB)',
        'Basic templates only',
        'Standard support'
      ],
      buttonText: 'Continue with Free',
      buttonStyle: 'border border-gray-300 dark:border-[#2A2A2A] text-gray-700 dark:text-[#CCCCCC] hover:bg-gray-50 dark:hover:bg-[#1A1A1A]'
    },
    {
      id: 'professional',
      name: 'Professional',
      price: '$24',
      originalPrice: '$29',
      period: 'per member / month',
      description: 'Best for growing photography businesses',
      popular: true,
      features: [
        'Unlimited projects and clients',
        'Advanced workflow automation', 
        'Custom branding and templates',
        'Client portal and proofing',
        'Advanced analytics',
        'Priority support',
        'Unlimited storage',
        'Team collaboration tools',
        'Invoice and contract management',
        'Equipment tracking'
      ],
      buttonText: 'Try Professional for 30 days',
      buttonStyle: 'bg-brand hover:bg-brand-hover text-black'
    }
  ];

  const handlePlanSelection = (plan) => {
    updateFormData({ plan: plan.id });
    if (plan.id === 'free') {
      // Skip payment for free plan
      onNext();
      onNext(); // Skip payment step entirely
    } else {
      onNext(); // Go to payment step
    }
  };

  return (
    <div className="px-8 py-12 max-w-5xl mx-auto text-center">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-12"
      >
        <h2 className="text-4xl font-bold text-white mb-6">
          Choose your Plan
        </h2>
        <p className="text-xl text-white/70">
          Select the best Klorah experience for your {formData.teamType === 'team' ? 'team' : 'workflow'}
        </p>
      </motion.div>

      <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
        {plans.map((plan, index) => (
          <motion.div
            key={plan.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className={`relative p-8 rounded-2xl border text-left ${
              plan.popular 
                ? 'border-brand bg-gradient-to-b from-[#FFB27D]/10 to-[#FFB27D]/5 shadow-lg shadow-orange-500/20' 
                : 'border-white/10 bg-white/5'
            }`}
          >
            {plan.popular && (
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <div className="flex items-center gap-1 bg-gradient-to-r from-[#FFB27D] to-[#FF9D5C] text-black px-3 py-1 rounded-full text-xs font-medium">
                  <StarIcon className="w-3 h-3" />
                  Most Popular
                </div>
              </div>
            )}

            <div className="mb-6">
              <h3 className="text-2xl font-bold text-white">
                {plan.name}
              </h3>
              <div className="flex items-baseline gap-3 mt-3">
                <span className="text-4xl font-bold text-white">
                  {plan.price}
                </span>
                {plan.originalPrice && (
                  <span className="text-xl text-white/40 line-through">
                    {plan.originalPrice}
                  </span>
                )}
              </div>
              <p className="text-sm text-white/60 mt-1">
                {plan.period}
              </p>
              <p className="text-base text-white/70 mt-3">
                {plan.description}
              </p>
            </div>

            <ul className="space-y-4 mb-8">
              {plan.features.map((feature, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckIcon className="w-5 h-5 text-brand mt-0.5 flex-shrink-0" />
                  <span className="text-base text-white/90">
                    {feature}
                  </span>
                </li>
              ))}
              {plan.limitations && plan.limitations.map((limitation, i) => (
                <li key={`limitation-${i}`} className="flex items-start gap-3 opacity-60">
                  <div className="w-5 h-5 mt-0.5 flex-shrink-0 flex items-center justify-center">
                    <div className="w-2 h-2 bg-white/40 rounded-full" />
                  </div>
                  <span className="text-base text-white/50">
                    {limitation}
                  </span>
                </li>
              ))}
            </ul>

            <button
              onClick={() => handlePlanSelection(plan)}
              className={`w-full py-3 px-4 rounded-xl font-medium transition-colors ${plan.buttonStyle}`}
            >
              {plan.buttonText}
            </button>

            {plan.id === 'professional' && (
              <p className="text-xs text-gray-500 dark:text-[#666666] mt-3 text-center">
                No commitment. Cancel anytime.
              </p>
            )}
          </motion.div>
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="mt-8"
      >
        <p className="text-sm text-gray-500 dark:text-[#666666]">
          All plans include a 30-day money-back guarantee
        </p>
      </motion.div>
    </div>
  );
};

export default PricingSelection;